import React, { useState } from 'react';
import { Builder } from '@builder.io/react';
import scrollToTop from '../../../../../hooks/ui/useScrollToTop';

const CardListWithVideoModal = ({ products }) => {
    const [showModal, setShowModal] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [expandedDescription, setExpandedDescription] = useState({});

    const getThumbnailUrl = (videoId) => {
        return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';
    };

    const cards = products && products.length > 0
        ? products.map((product, index) => ({
            id: index + 1,
            title: product.title || '',
            description: product.description || '',
            image: getThumbnailUrl(product.videoId),
            videoId: product.videoId || ''
        }))
        : [];

    const openVideoModal = (videoData) => {
        const videoId = videoData.videoId || videoData;
        if (videoId) {
            const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&modestbranding=1&rel=0&loop=1&playlist=${videoId}`;
            setVideoUrl(embedUrl);
            setShowModal(true);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setVideoUrl('');
    };

    return (
        <div className="md:container md:mx-auto mt-5 pb-10 px-1 relative">
            <button
                onClick={() => scrollToTop()}
                className="z-10 fixed bottom-24 right-6 md:right-10 bg-gradient-to-r from-[#EE1059] to-[#d91e4d] text-white font-bold py-2 md:py-4 px-4 md:px-6 rounded-full shadow-2xl transition-all duration-300 ease-in-out hover:scale-110 hover:shadow-xl active:scale-95"
            >
                <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold whitespace-nowrap">
                        QUICK DONATE
                    </span>
                </div>
            </button>

            {cards.length === 0 ? (
                <div className="text-center py-10">
                    <p className="text-gray-500">No videos available. Please add some videos in Builder.io.</p>
                </div>
            ) : (
                <div className="relative">
                    <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 justify-items-center sm:px-0">
                        {cards.map((card, index) => {
                            return (
                                <div
                                    key={card.id || index}
                                    className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform duration-300 hover:scale-105 hover:shadow-lg w-full mb-2"
                                    onClick={() => openVideoModal(card)}
                                >
                                    <div className="relative h-64">
                                        <img
                                            src={card.image}
                                            alt={card.title}
                                            className="w-full h-full object-cover object-top"
                                        />

                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90  via-black/30 to-transparent"></div>

                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity duration-300">
                                            <div className="bg-red-600 rounded-full p-3 flex items-center justify-center">
                                                <svg
                                                    className="w-8 h-8 text-white"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path d="M8 5v10l8-5z" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="px-4 pt-2 pb-1 bg-white flex flex-col">
                                        <h3 className="text-[16] font-semibold text-[#4FC3F7] leading-tight overflow-hidden"
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                textOverflow: 'ellipsis'
                                            }}>
                                            {card.title}
                                        </h3>
                                        <p className="text-sm font-normal text-gray-600 leading-snug mt-1 pb-1"
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: expandedDescription[card.id] ? 'unset' : 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxHeight: expandedDescription[card.id] ? 'unset' : '4.2em',
                                                lineHeight: '1.4em'
                                            }}>
                                            {card.description}
                                        </p>
                                        {card.description.split(/\s+/).length > 20 && (
                                            <button
                                                className="text-[#4FC3F7] text-sm mt-1 text-left"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedDescription(prevState => ({
                                                        ...prevState,
                                                        [card.id]: !prevState[card.id]
                                                    }));
                                                }}
                                            >
                                                {expandedDescription[card.id] ? 'Show Less' : 'Read More'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                            )
                        })}
                    </div>
                </div>
            )}

            {showModal && (
                <div
                    className="fixed flex flex-col left-0 top-0 z-[1055] h-full w-full overflow-y-auto overflow-x-hidden outline-none bg-black bg-opacity-30 sm:bg-opacity-75 justify-center items-center p-1 sm:p-2"
                    tabIndex={-1}
                    aria-hidden="true"
                    onClick={closeModal}
                >
                    <div
                        className="overflow-hidden border-2 rounded-2xl border-white bg-black sm:bg-white shadow-2xl pointer-events-none relative opacity-100 transition-all duration-300 ease-in-out w-full max-w-[90vw] h-[50vh] max-h-[90vh] sm:h-auto sm:max-w-6xl mx-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="pointer-events-auto relative flex flex-col w-full h-full sm:h-auto">
                            <div className="relative h-full sm:h-auto">
                                <button
                                    onClick={closeModal}
                                    className="absolute top-[3px] right-[3px] bg-red-600 text-white rounded-full p-2 z-20 shadow-lg  transition-colors"
                                    aria-label="Close video modal"
                                >
                                    <svg
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M6 18L18 6M6 6l12 12"
                                        ></path>
                                    </svg>
                                </button>
                                <iframe
                                    className="video w-full h-full sm:h-64 md:h-96 lg:h-[500px] rounded-none sm:rounded-lg"
                                    width="100%"
                                    height="100%"
                                    title="Video Modal"
                                    sandbox="allow-same-origin allow-forms allow-popups allow-scripts allow-presentation"
                                    src={videoUrl}
                                    allow="autoplay; fullscreen"
                                    frameBorder="0"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
Builder.registerComponent(CardListWithVideoModal, {
    name: 'CardListWithVideoModal',
    inputs: [
        {
            name: 'products',
            type: 'list',
            subFields: [
                {
                    name: 'title',
                    type: 'string',
                    required: true,
                    defaultValue: 'Video Title',
                    helperText: 'The title of the video card'
                },
                {
                    name: 'description',
                    type: 'longText',
                    required: true,
                    defaultValue: 'Video description goes here...',
                    helperText: 'Brief description of what the video is about'
                },
                {
                    name: 'videoId',
                    type: 'string',
                    required: true,
                    defaultValue: 'SqcY0GlETPk',
                    helperText: 'YouTube video ID (e.g., SqcY0GlETPk). Thumbnail will be generated automatically from the ID.'
                }
            ]
        }
    ]
})
export default CardListWithVideoModal;